/**
 * Browser-side WebAuthn API wrappers for passkey registration and login.
 *
 * Converts between base64url-encoded JSON options from the server
 * and the ArrayBuffer-based WebAuthn browser API, then serializes
 * credential results back into a shape suitable for gramjs MTProto types
 * (InputPasskeyCredentialPublicKey with InputPasskeyResponseRegister / Login).
 */

// -- Types -------------------------------------------------------------------

export interface PasskeyRegistrationResult {
  id: string;
  rawId: string;
  /** base64url-encoded clientDataJSON bytes */
  clientDataJSON: string;
  /** base64url-encoded attestationObject */
  attestationObject: string;
}

export interface PasskeyLoginResult {
  id: string;
  rawId: string;
  /** base64url-encoded clientDataJSON bytes */
  clientDataJSON: string;
  /** base64url-encoded authenticatorData */
  authenticatorData: string;
  /** base64url-encoded signature */
  signature: string;
  /** base64url-encoded userHandle */
  userHandle: string;
}

// -- Helpers -----------------------------------------------------------------

/**
 * Convert a base64url-encoded string (no padding, URL-safe alphabet) to an ArrayBuffer.
 */
export function base64urlToBuffer(base64url: string): ArrayBuffer {
  // Restore standard base64 from base64url
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

  // Re-add padding if necessary
  const paddingNeeded = (4 - (base64.length % 4)) % 4;
  base64 += '='.repeat(paddingNeeded);

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Convert an ArrayBuffer (or Uint8Array buffer) to a base64url-encoded string
 * (no padding, URL-safe alphabet).
 */
export function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// -- Feature detection -------------------------------------------------------

/**
 * Check whether the browser supports WebAuthn (PublicKeyCredential API).
 */
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined';
}

// -- Registration ------------------------------------------------------------

/**
 * Create a new passkey (registration / attestation ceremony).
 *
 * @param optionsJson - JSON string received from the server
 *   (`account.initPasskeyRegistration` response, `DataJSON.data`).
 *   Must contain a top-level `publicKey` object with base64url-encoded
 *   `challenge`, `user.id`, and optional `excludeCredentials[].id`.
 *
 * @returns Object ready to be mapped to gramjs
 *   `InputPasskeyCredentialPublicKey` + `InputPasskeyResponseRegister`.
 */
export async function createPasskey(optionsJson: string): Promise<PasskeyRegistrationResult> {
  const options = JSON.parse(optionsJson);
  const { publicKey } = options;

  // Convert base64url fields to ArrayBuffer as required by the WebAuthn API
  publicKey.challenge = base64urlToBuffer(publicKey.challenge);

  if (publicKey.user?.id) {
    publicKey.user.id = base64urlToBuffer(publicKey.user.id);
  }

  if (publicKey.excludeCredentials) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map(
      (cred: { id: string; type: string; transports?: string[] }) => ({
        ...cred,
        id: base64urlToBuffer(cred.id),
      }),
    );
  }

  const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('WebAuthn: navigator.credentials.create() returned null');
  }

  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    clientDataJSON: bufferToBase64url(response.clientDataJSON),
    attestationObject: bufferToBase64url(response.attestationObject),
  };
}

// -- Login -------------------------------------------------------------------

/**
 * Authenticate with an existing passkey (assertion ceremony).
 *
 * @param optionsJson - JSON string received from the server
 *   (`auth.initPasskeyLogin` response, `DataJSON.data`).
 *   Must contain a top-level `publicKey` object with a base64url-encoded
 *   `challenge` and optional `allowCredentials[].id`.
 *
 * @returns Object ready to be mapped to gramjs
 *   `InputPasskeyCredentialPublicKey` + `InputPasskeyResponseLogin`.
 */
export async function loginWithPasskey(optionsJson: string): Promise<PasskeyLoginResult> {
  const options = JSON.parse(optionsJson);
  const { publicKey } = options;

  // Convert base64url fields to ArrayBuffer
  publicKey.challenge = base64urlToBuffer(publicKey.challenge);

  if (publicKey.allowCredentials) {
    publicKey.allowCredentials = publicKey.allowCredentials.map(
      (cred: { id: string; type: string; transports?: string[] }) => ({
        ...cred,
        id: base64urlToBuffer(cred.id),
      }),
    );
  }

  const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('WebAuthn: navigator.credentials.get() returned null');
  }

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    clientDataJSON: bufferToBase64url(response.clientDataJSON),
    authenticatorData: bufferToBase64url(response.authenticatorData),
    signature: bufferToBase64url(response.signature),
    userHandle: response.userHandle
      ? bufferToBase64url(response.userHandle)
      : '',
  };
}
